import { useRef, useCallback, useEffect, useState } from 'react';
import { CanvasArea } from '../components/CanvasArea';
import { EditorToolbar } from '../components/EditorToolbar';
import { EditorTabBar } from '../components/EditorTabBar';
import { ToolPalette } from '../components/ToolPalette';
import { PropertiesPanel } from '../components/PropertiesPanel';
import { StatusBar } from '../components/StatusBar';
import { ExportDialog } from '../components/ExportDialog';
import { ConfirmCloseDialog, type SaveDestination } from '../components/ConfirmCloseDialog';
import { IconLegendDialog } from '../components/IconLegendDialog';
import { FiasImagePickerDialog } from '../components/FiasImagePickerDialog';
import { LayersPanel } from '../components/LayersPanel';
import { useImageEditorState } from '../hooks/useImageEditorState';
import { useFabricCanvas } from '../hooks/useFabricCanvas';
import { useCanvasHistory, SERIALIZED_PROPS } from '../hooks/useCanvasHistory';
import { useImageExport } from '../hooks/useImageExport';
import { useIsLandscape } from '../hooks/useIsLandscape';
import { useDocumentTabs } from '../hooks/useDocumentTabs';
import { loadSession, saveSession, type PersistedSession } from '../lib/session-store';
import type { EditorTool, ExportSettings, Layer, LayerType } from '../types';

export function EditorPage() {
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isLandscape = useIsLandscape();
  const [hasImage, setHasImage] = useState(false);
  const layerIdSeqRef = useRef(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [fiasPickerOpen, setFiasPickerOpen] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [removeBackgroundError, setRemoveBackgroundError] = useState<string | null>(null);

  // Close-confirmation flow. When a dirty tab's X is clicked, the editor
  // first switches to that tab (so its content is on the canvas) and shows
  // the dialog. The Save / Save-As paths run through ExportDialog so the
  // close happens AFTER the export resolves.
  const [confirmCloseTabId, setConfirmCloseTabId] = useState<string | null>(null);
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [exportDialogDestination, setExportDialogDestination] = useState<SaveDestination>('local');
  /** User-facing error message from the most recent save attempt, surfaced
   *  in the close-confirm dialog so the user can react to FSA permission
   *  revocations, network failures, quota exceeded, etc. instead of seeing
   *  the dialog hang. */
  const [saveError, setSaveError] = useState<string | null>(null);
  // When non-null, the currently-mounted ExportDialog should close this tab
  // after a successful export. Captured at the moment Save-As is clicked so
  // it survives the dialog closing on cancel without leaking into the next
  // unrelated export.
  const closeAfterExportTabIdRef = useRef<string | null>(null);

  const {
    state,
    setActiveTool,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateMarqueeSettings,
    updateFilterSettings,
    setZoom,
    setCanvasDimensions,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    toggleLayersPanel,
    setCursorInfo,
    setCursorPosition,
    addLayer,
    removeLayer,
    renameLayer,
    toggleLayerVisibility,
    setActiveLayer,
    reorderLayer,
    resetLayers,
  } = useImageEditorState();

  interface LayersHistoryExtra {
    layers: Layer[];
    activeLayerId: string | null;
  }
  const history = useCanvasHistory<LayersHistoryExtra>({ maxHistory: 50 });

  // Refs that always hold the latest layer state so the history hook's
  // get-extra callback can be registered once and read fresh values.
  const layersStateRef = useRef<LayersHistoryExtra>({
    layers: state.layers,
    activeLayerId: state.activeLayerId,
  });
  layersStateRef.current = { layers: state.layers, activeLayerId: state.activeLayerId };

  useEffect(() => {
    history.setExtraStateAccess({
      get: () => layersStateRef.current,
      set: ({ layers, activeLayerId }) => resetLayers(layers, activeLayerId),
    });
    return () => history.setExtraStateAccess(null);
  }, [history, resetLayers]);

  // Set when a user-action handler changes layer state — useEffect below
  // captures the post-commit snapshot so the saved entry reflects the new state.
  const layerSnapshotPendingRef = useRef(false);

  const canvas = useFabricCanvas({
    canvasElRef,
    wrapperRef,
    onObjectModified: () => {
      markDirty();
      history.saveSnapshot();
    },
    onSelectionChanged: setSelection,
    onLayerSelected: (layerId) => {
      // Sync the panel highlight to the canvas selection. We don't clear the
      // active layer when canvas-selection clears (matches Photoshop).
      if (layerId) setActiveLayer(layerId);
    },
    onShapeToolClick: (point) => {
      // User clicked empty canvas with the Shape tool active — drop a fresh
      // shape at the click position using the current settings.
      const layer = makeLayer('shape');
      addLayer(layer, true);
      canvas.addShape(state.shapeSettings.type, state.shapeSettings, layer.id, point);
      layerSnapshotPendingRef.current = true;
    },
    onTextToolClick: (point) => {
      // Same behavior for the Text tool.
      const layer = makeLayer('text');
      addLayer(layer, true);
      canvas.addText('add text', state.textSettings, layer.id, point);
      layerSnapshotPendingRef.current = true;
    },
    onCanvasDimensions: setCanvasDimensions,
    // `color` is null for non-eyedropper tools — keep the displayed color
    // pinned to whatever was last picked instead of overwriting it.
    onCursorMove: (x, y, color) => {
      if (color === null) setCursorPosition(x, y);
      else setCursorInfo(x, y, color);
    },
    onColorPicked: (color) => {
      // Selecting a color via the canvas eyedropper sets it as the brush /
      // fill color. The preceding mouse:move already updated
      // `state.colorUnderCursor`, so the status-bar swatch is correct.
      updateBrushSettings({ color });
    },
    onPathCreated: () => {
      markDirty();
      history.saveSnapshot();
    },
    onZoomChanged: setZoom,
  });

  // Connect canvas to history
  useEffect(() => {
    if (canvas.canvasRef.current) {
      history.setCanvas(
        canvas.canvasRef.current as unknown as Parameters<typeof history.setCanvas>[0],
      );
    }
  }, [canvas.canvasRef, history]);

  const { isExporting, exportToLocal, exportToFias } = useImageExport({
    exportToDataURL: canvas.exportToDataURL,
    exportToBlob: canvas.exportToBlob,
  });

  // Multi-tab document state. Each tab represents one open file. Active tab
  // = live editor state; inactive tabs hold serialized PersistedSession
  // snapshots that get re-applied to the editor on tab switch.
  const tabs = useDocumentTabs();

  // Snapshot the current editor state into a PersistedSession-shaped blob.
  // Returns null if there's nothing to snapshot (canvas not ready).
  const buildSnapshot = useCallback((): PersistedSession | null => {
    const json = canvas.toJSON(SERIALIZED_PROPS);
    if (!json) return null;
    const { states, index } = history.getHistory();
    return {
      canvasJSON: json,
      fileName: state.fileName,
      fileId: state.fileId,
      filters: state.filterSettings,
      viewportTransform: canvas.getViewportTransform() ?? undefined,
      layers: state.layers,
      activeLayerId: state.activeLayerId,
      historyStates: states.length > 0 ? states : undefined,
      historyIndex: states.length > 0 ? index : undefined,
      savedAt: Date.now(),
    };
  }, [
    canvas,
    history,
    state.fileName,
    state.fileId,
    state.filterSettings,
    state.layers,
    state.activeLayerId,
  ]);

  // Restore a PersistedSession into the editor — inverse of buildSnapshot.
  // Used both by the IndexedDB restore-on-mount flow and by tab switching.
  const applySnapshot = useCallback(
    async (session: PersistedSession) => {
      try {
        await canvas.loadFromJSON(session.canvasJSON);
        const restoredZoom =
          session.viewportTransform && session.viewportTransform.length === 6
            ? canvas.setViewportTransform(session.viewportTransform)
            : (canvas.fitToContainer() ?? 1);
        setFileInfo(session.fileName, session.fileId);
        updateFilterSettings(session.filters);
        setZoom(restoredZoom);
        if (session.layers) {
          resetLayers(session.layers, session.activeLayerId ?? null);
        }
        setHasImage(true);
        markClean();
        if (session.historyStates && session.historyStates.length > 0) {
          await history.restoreHistory(
            session.historyStates as { canvasJSON: object; extra?: LayersHistoryExtra }[],
            session.historyIndex ?? session.historyStates.length - 1,
          );
        } else {
          history.clearHistory();
          history.saveSnapshot();
        }
      } catch {
        // If apply fails (corrupt snapshot), leave the editor in whatever
        // state it managed to reach — better than crashing.
      }
    },
    [canvas, history, setFileInfo, updateFilterSettings, setZoom, resetLayers, markClean],
  );

  // Reset the editor to its empty/no-image state. Used when switching to a
  // freshly-created tab that hasn't had a file loaded into it yet, or when
  // the last tab is closed.
  const resetEditorToEmpty = useCallback(() => {
    canvas.clearCanvas();
    resetLayers([], null);
    setFileInfo('Untitled', null);
    history.clearHistory();
    setHasImage(false);
    markClean();
  }, [canvas, resetLayers, setFileInfo, history, markClean]);

  const nextLayerId = useCallback(() => {
    layerIdSeqRef.current += 1;
    return `layer_${Date.now()}_${layerIdSeqRef.current}`;
  }, []);

  const makeLayer = useCallback(
    (type: LayerType, customName?: string): Layer => {
      const typeName: Record<LayerType, string> = {
        image: 'Image',
        text: 'Text',
        shape: 'Shape',
        paint: 'Drawing',
        empty: 'Layer',
      };
      const count = state.layers.filter((l) => l.type === type).length + 1;
      return {
        id: nextLayerId(),
        name: customName ?? `${typeName[type]} ${count}`,
        type,
        visible: true,
      };
    },
    [state.layers, nextLayerId],
  );

  // Handle file loading. Always opens the file in a NEW tab — both the
  // toolbar Open button and the tab bar's + button funnel through here, so
  // the user's existing work is never silently overwritten. When `handle`
  // is provided (browsers with the File System Access API), the tab keeps
  // a reference so a later Save can overwrite the original file in place.
  const loadFile = useCallback(
    (file: File, handle?: FileSystemFileHandle | null) => {
      const displayName = file.name.replace(/\.[^.]+$/, '');
      tabs.addTab({ fileName: displayName, fileId: null, fileHandle: handle ?? null });
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        const fitZoom = await canvas.loadImage(dataUrl);
        const bgLayer: Layer = {
          id: nextLayerId(),
          name: 'Background',
          type: 'image',
          visible: true,
        };
        canvas.setBackgroundLayerId(bgLayer.id);
        resetLayers([bgLayer], bgLayer.id);
        setZoom(fitZoom);
        setFileInfo(displayName, null);
        setHasImage(true);
        markClean();
        history.clearHistory();
        history.saveSnapshot();
      };
      reader.readAsDataURL(file);
    },
    [canvas, setFileInfo, markClean, history, nextLayerId, resetLayers, setZoom, tabs],
  );

  // Layer action handlers. Each mutates state/canvas and flags the snapshot
  // useEffect (above) to capture once the React commit has flushed.
  const handleAddLayer = useCallback(() => {
    const layer = makeLayer('empty');
    addLayer(layer, true);
    layerSnapshotPendingRef.current = true;
  }, [addLayer, makeLayer]);

  const handleSelectLayer = useCallback(
    (id: string) => {
      setActiveLayer(id);
      canvas.selectLayerObjects(id);
      // Selection changes are not snapshotted — matches Photoshop behavior.
    },
    [setActiveLayer, canvas],
  );

  const handleToggleLayerVisibility = useCallback(
    (id: string) => {
      const layer = state.layers.find((l) => l.id === id);
      if (!layer) return;
      canvas.setLayerVisibility(id, !layer.visible);
      toggleLayerVisibility(id);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, state.layers, toggleLayerVisibility],
  );

  const handleRenameLayer = useCallback(
    (id: string, name: string) => {
      renameLayer(id, name);
      layerSnapshotPendingRef.current = true;
    },
    [renameLayer],
  );

  const handleDeleteLayer = useCallback(
    (id: string) => {
      canvas.removeLayerObjects(id);
      removeLayer(id);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, removeLayer],
  );

  const handleReorderLayer = useCallback(
    (fromIndex: number, toIndex: number) => {
      reorderLayer(fromIndex, toIndex);
      layerSnapshotPendingRef.current = true;
    },
    [reorderLayer],
  );

  // Keep the canvas z-order in sync with the layer list. Front-of-list = top of canvas.
  useEffect(() => {
    canvas.applyLayerOrder(state.layers.map((l) => l.id));
  }, [state.layers, canvas]);

  // Capture history snapshot after a user-action layer change has committed.
  // Triggered by the pending flag set in the layer handlers; this guarantees
  // the snapshot reflects the post-update layer state.
  useEffect(() => {
    if (!layerSnapshotPendingRef.current) return;
    layerSnapshotPendingRef.current = false;
    history.saveSnapshot();
    markDirty();
  }, [state.layers, history, markDirty]);

  const handleApplyCrop = useCallback(() => {
    if (canvas.applyActiveCrop()) {
      markDirty();
      history.saveSnapshot();
    }
  }, [canvas, markDirty, history]);

  // Cut / Copy / Paste — toolbar buttons + Ctrl+X/C/V shortcuts. Cut and
  // Copy operate on the marqueed region of the active layer's paint
  // surface; Paste creates a new "Pasted" layer placed at the original
  // bbox so the pixels return to where they were copied from.
  const handleCopy = useCallback(() => {
    canvas.copySelection();
  }, [canvas]);

  const handleCut = useCallback(() => {
    if (canvas.cutSelection()) {
      markDirty();
      history.saveSnapshot();
    }
  }, [canvas, markDirty, history]);

  const handlePaste = useCallback(async () => {
    const item = canvas.getClipboard();
    if (!item) return;
    const blob = await new Promise<Blob | null>((resolve) => {
      item.canvas.toBlob(resolve, 'image/png');
    });
    if (!blob) return;
    const layer = makeLayer('image', 'Pasted');
    addLayer(layer, true);
    const img = await canvas.addImageLayer(blob, layer.id);
    if (img) {
      img.set({ left: item.left, top: item.top, originX: 'left', originY: 'top' });
      img.setCoords();
    }
    // Flag the snapshot effect to capture once React commits the new layer
    // state. We don't call markDirty() / history.saveSnapshot() here — the
    // pending-flag effect handles both, so doing it twice was producing
    // duplicate history entries and requiring two Ctrl+Z to undo a paste.
    layerSnapshotPendingRef.current = true;
  }, [canvas, addLayer, makeLayer]);

  // Tool switching
  const handleToolSelect = useCallback(
    (tool: EditorTool) => {
      setActiveTool(tool);
      canvas.setTool(tool, state.brushSettings);

      // Auto-add text or shape on tool select; each gets its own layer.
      if (tool === 'text') {
        const layer = makeLayer('text');
        addLayer(layer, true);
        canvas.addText('add text', state.textSettings, layer.id);
        layerSnapshotPendingRef.current = true;
      }
      if (tool === 'shape') {
        const layer = makeLayer('shape');
        addLayer(layer, true);
        canvas.addShape(state.shapeSettings.type, state.shapeSettings, layer.id);
        layerSnapshotPendingRef.current = true;
      }
    },
    [
      setActiveTool,
      canvas,
      state.brushSettings,
      state.textSettings,
      state.shapeSettings,
      addLayer,
      makeLayer,
    ],
  );

  // Apply filters when settings change
  useEffect(() => {
    canvas.applyFilters(state.filterSettings);
  }, [state.filterSettings, canvas]);

  useEffect(() => {
    canvas.setMarqueeShape(state.marqueeSettings.shape);
  }, [state.marqueeSettings.shape, canvas]);

  useEffect(() => {
    canvas.setForceSquare(state.marqueeSettings.forceSquare);
  }, [state.marqueeSettings.forceSquare, canvas]);

  useEffect(() => {
    canvas.setMarqueeColor(state.marqueeSettings.color);
  }, [state.marqueeSettings.color, canvas]);

  useEffect(() => {
    canvas.setWandSettings(state.marqueeSettings.tolerance, state.marqueeSettings.contiguous);
  }, [state.marqueeSettings.tolerance, state.marqueeSettings.contiguous, canvas]);

  // Push the active layer id into the canvas hook so new draw strokes get
  // tagged with it (and appear under that layer in the panel).
  useEffect(() => {
    canvas.setActiveLayerId(state.activeLayerId);
  }, [state.activeLayerId, canvas]);

  // Push the latest brush color so the Fill tool can use it on click.
  useEffect(() => {
    canvas.setBrushColor(state.brushSettings.color);
  }, [state.brushSettings.color, canvas]);

  // Live-update the active drawing brush whenever any brush setting changes,
  // so the Draw / Eraser tool's next stroke uses the current values without
  // requiring a tool re-pick.
  useEffect(() => {
    canvas.updateBrush(state.brushSettings);
  }, [state.brushSettings, canvas]);

  // Bidirectional shape-settings sync:
  //   1) When `state.activeLayerId` changes, read the active shape's actual
  //      properties and mirror them into `state.shapeSettings`. The Shape
  //      panel always shows the selected shape's properties.
  //   2) When `state.shapeSettings` changes (e.g., user moved a slider),
  //      apply those settings to the active shape.
  // Both effects use prev-refs to guard against the `canvas` dep changing
  // identity on every render (it's a fresh object literal from
  // `useFabricCanvas`). Without these guards, the sync effect dispatches on
  // every render → infinite loop. The `syncingFromCanvasRef` flag prevents
  // the apply-effect from re-applying settings just read from the canvas.
  const prevShapeSettingsRef = useRef(state.shapeSettings);
  const prevActiveLayerIdForShapeRef = useRef<string | null | undefined>(undefined);
  const syncingShapeFromCanvasRef = useRef(false);

  useEffect(() => {
    if (prevActiveLayerIdForShapeRef.current === state.activeLayerId) return;
    prevActiveLayerIdForShapeRef.current = state.activeLayerId;
    const fromCanvas = canvas.getActiveShapeSettings();
    if (!fromCanvas) return;
    syncingShapeFromCanvasRef.current = true;
    updateShapeSettings(fromCanvas);
  }, [state.activeLayerId, canvas, updateShapeSettings]);

  useEffect(() => {
    if (prevShapeSettingsRef.current === state.shapeSettings) return;
    prevShapeSettingsRef.current = state.shapeSettings;
    if (syncingShapeFromCanvasRef.current) {
      // The latest shapeSettings update came from reading the active shape;
      // skip the round-trip apply.
      syncingShapeFromCanvasRef.current = false;
      return;
    }
    canvas.updateActiveShape(state.shapeSettings);
  }, [state.shapeSettings, canvas]);

  // Same bidirectional sync for the Text tool's settings.
  const prevTextSettingsRef = useRef(state.textSettings);
  const prevActiveLayerIdForTextRef = useRef<string | null | undefined>(undefined);
  const syncingTextFromCanvasRef = useRef(false);

  useEffect(() => {
    if (prevActiveLayerIdForTextRef.current === state.activeLayerId) return;
    prevActiveLayerIdForTextRef.current = state.activeLayerId;
    const fromCanvas = canvas.getActiveTextSettings();
    if (!fromCanvas) return;
    syncingTextFromCanvasRef.current = true;
    updateTextSettings(fromCanvas);
  }, [state.activeLayerId, canvas, updateTextSettings]);

  useEffect(() => {
    if (prevTextSettingsRef.current === state.textSettings) return;
    prevTextSettingsRef.current = state.textSettings;
    if (syncingTextFromCanvasRef.current) {
      syncingTextFromCanvasRef.current = false;
      return;
    }
    canvas.updateActiveText(state.textSettings);
  }, [state.textSettings, canvas]);

  // Clipboard paste — extracts an image from the OS clipboard and creates a new image layer.
  useEffect(() => {
    const onPaste = async (e: ClipboardEvent) => {
      // Skip if focus is in an input/textarea (the user is pasting text into a form)
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (!blob) continue;
          e.preventDefault();
          const layer = makeLayer('image');
          addLayer(layer, true);
          await canvas.addImageLayer(blob, layer.id);
          layerSnapshotPendingRef.current = true;
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [canvas, addLayer, makeLayer]);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    void (async () => {
      const session = await loadSession();
      if (!session) return;
      // Seed the session as the new tab's initial snapshot. The tab-switch
      // effect (below) will pick it up on the next render and call
      // applySnapshot — this single source of truth avoids racing
      // resetEditorToEmpty when the effect fires concurrently with a
      // direct applySnapshot call here.
      tabs.addTab({
        fileName: session.fileName,
        fileId: session.fileId,
        snapshot: session,
      });
    })();
  }, []);

  // Tab switching. When the active tab id changes, save the outgoing tab's
  // editor state and apply the incoming tab's snapshot (or reset the editor
  // for a freshly-created tab with no snapshot yet).
  //
  // We list every closed-over value as a dep, but the body short-circuits
  // on `prevId === newId` so non-id changes (e.g. setTabSnapshot updating
  // tabs.tabs) cost only the dep comparison.
  const prevActiveTabIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevId = prevActiveTabIdRef.current;
    const newId = tabs.activeTabId;
    if (prevId === newId) return;
    prevActiveTabIdRef.current = newId;

    void (async () => {
      // 1. Capture outgoing tab's state.
      if (prevId !== null && hasImage) {
        const outgoing = buildSnapshot();
        if (outgoing) tabs.setTabSnapshot(prevId, outgoing);
      }

      // 2. Restore incoming tab.
      if (newId === null) {
        // No active tab — last tab was closed.
        resetEditorToEmpty();
        return;
      }
      const incoming = tabs.tabs.find((t) => t.id === newId);
      if (incoming?.snapshot) {
        await applySnapshot(incoming.snapshot);
        // Snapshot is now live in the editor — clear it so the inactive
        // copy doesn't drift.
        tabs.setTabSnapshot(newId, null);
      } else {
        // Freshly-created tab with no content yet. Reset and wait for
        // loadFile / handleFiasImageSelected to populate the editor.
        resetEditorToEmpty();
      }
    })();
  }, [
    tabs.activeTabId,
    tabs.tabs,
    tabs.setTabSnapshot,
    hasImage,
    buildSnapshot,
    applySnapshot,
    resetEditorToEmpty,
  ]);

  // Mirror file-related editor state onto the active tab so the tab label
  // reflects renames + dirty status without the user opening the tab.
  useEffect(() => {
    if (!tabs.activeTabId) return;
    tabs.updateActiveTabMetadata({
      fileName: state.fileName,
      fileId: state.fileId,
      isDirty: state.isDirty,
    });
  }, [state.fileName, state.fileId, state.isDirty, tabs.activeTabId]);

  // Debounced session save. Keyed on canvas mutations (history counts), file
  // metadata, filters, and zoom — so the user's last view (zoom + pan) is
  // preserved when paired with any other change. Persists only the active
  // tab's state — multi-tab persistence is a planned follow-up.
  useEffect(() => {
    if (!hasImage) return;
    const timer = setTimeout(() => {
      const snapshot = buildSnapshot();
      if (snapshot) void saveSession(snapshot);
    }, 800);
    return () => clearTimeout(timer);
  }, [
    buildSnapshot,
    hasImage,
    state.fileName,
    state.fileId,
    state.filterSettings,
    state.zoom,
    state.layers,
    state.activeLayerId,
    history.undoCount,
    history.redoCount,
  ]);

  // Keyboard shortcuts. Stash the dependent handlers in a ref so the
  // window listener can be registered exactly once with `[]` deps —
  // otherwise it would tear down + re-attach on every tool click (which
  // toggles state.activeTool, which used to be a dep), creating a tiny
  // window where keypresses are dropped.
  const keyHandlersRef = useRef({
    undo: () => history.undoCanvas(),
    redo: () => history.redoCanvas(),
    deleteSelected: () => canvas.deleteSelected(),
    selectAll: () => canvas.selectAll(),
    openSaveAs: () => setExportDialogOpen(true),
    copy: handleCopy,
    cut: handleCut,
    paste: handlePaste,
    applyCrop: handleApplyCrop,
    selectTool: handleToolSelect,
    activeTool: state.activeTool,
  });
  keyHandlersRef.current = {
    undo: () => history.undoCanvas(),
    redo: () => history.redoCanvas(),
    deleteSelected: () => canvas.deleteSelected(),
    selectAll: () => canvas.selectAll(),
    openSaveAs: () => setExportDialogOpen(true),
    copy: handleCopy,
    cut: handleCut,
    paste: handlePaste,
    applyCrop: handleApplyCrop,
    selectTool: handleToolSelect,
    activeTool: state.activeTool,
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      const h = keyHandlersRef.current;
      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        h.undo();
      } else if (ctrl && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        h.redo();
      } else if (ctrl && e.key === 'y') {
        e.preventDefault();
        h.redo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        h.deleteSelected();
      } else if (ctrl && e.key === 'a') {
        e.preventDefault();
        h.selectAll();
      } else if (ctrl && e.key === 's') {
        e.preventDefault();
        h.openSaveAs();
      } else if (ctrl && e.key === 'c') {
        e.preventDefault();
        h.copy();
      } else if (ctrl && e.key === 'x') {
        e.preventDefault();
        h.cut();
      } else if (ctrl && e.key === 'v') {
        e.preventDefault();
        void h.paste();
      } else if (e.key === 'Enter' && h.activeTool === 'crop') {
        e.preventDefault();
        h.applyCrop();
      }

      // Tool shortcuts
      if (!ctrl && !e.shiftKey) {
        const toolMap: Record<string, EditorTool> = {
          v: 'move',
          m: 'marquee',
          c: 'crop',
          b: 'draw',
          t: 'text',
          u: 'shape',
          e: 'eraser',
          g: 'fill',
          i: 'eyedropper',
        };
        const tool = toolMap[e.key.toLowerCase()];
        if (tool) h.selectTool(tool);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close the close-after-export tab if one was set when Save-As triggered
  // this export. Captured to a local var first so the ref can be cleared
  // immediately, preventing a re-trigger if the dialog is reopened later.
  const consumeCloseAfterExport = useCallback(() => {
    const id = closeAfterExportTabIdRef.current;
    closeAfterExportTabIdRef.current = null;
    if (id) tabs.closeTab(id);
  }, [tabs]);

  const handleExportLocal = useCallback(
    async (fileName: string, format: 'png' | 'jpeg' | 'webp', quality: number) => {
      await exportToLocal(fileName, format, quality);
      markClean();
      consumeCloseAfterExport();
    },
    [exportToLocal, markClean, consumeCloseAfterExport],
  );

  const handleExportFias = useCallback(
    async (settings: ExportSettings, fileName: string) => {
      const dataUrl = canvas.exportToDataURL(settings.format, settings.quality / 100);
      const result = await exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: {
          format: settings.format,
          quality: settings.quality,
          width: settings.width,
          height: settings.height,
          maintainAspectRatio: settings.maintainAspectRatio,
        },
        saveAs: { fileName },
      });
      if (result) {
        markClean();
        consumeCloseAfterExport();
      }
    },
    [canvas, exportToFias, markClean, consumeCloseAfterExport],
  );

  // Prefer the File System Access API so a later Save can overwrite the
  // original file in place. In Firefox / Safari (no FSA), fall back to the
  // hidden `<input type="file">` — that path can't capture a writable
  // handle, so Save will degrade to a download.
  const pickAndOpenLocalFile = useCallback(async () => {
    type ShowOpenFilePicker = (opts?: {
      types?: { description: string; accept: Record<string, string[]> }[];
      multiple?: boolean;
      excludeAcceptAllOption?: boolean;
    }) => Promise<FileSystemFileHandle[]>;
    const picker = (window as unknown as { showOpenFilePicker?: ShowOpenFilePicker })
      .showOpenFilePicker;
    if (!picker) {
      fileInputRef.current?.click();
      return;
    }
    try {
      const [handle] = await picker({
        types: [
          {
            description: 'Image',
            accept: {
              'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'],
            },
          },
        ],
        multiple: false,
      });
      if (!handle) return;
      const file = await handle.getFile();
      loadFile(file, handle);
    } catch (err) {
      // User dismissed the picker (AbortError) — silently abort.
      // Anything else is unexpected; surface to the console for debugging
      // without breaking the UI.
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        console.warn('[image-editor] showOpenFilePicker failed:', err);
      }
    }
  }, [loadFile]);

  const handleOpenFile = useCallback(() => {
    void pickAndOpenLocalFile();
  }, [pickAndOpenLocalFile]);

  const handleOpenFias = useCallback(() => {
    setFiasPickerOpen(true);
  }, []);

  // File menu — `New` creates a blank 1024×1024 white canvas in a new tab,
  // routed through loadFile so it follows the same per-tab plumbing as a
  // user-opened image.
  const handleNewBlankFile = useCallback(() => {
    const W = 1024;
    const H = 1024;
    const offscreen = document.createElement('canvas');
    offscreen.width = W;
    offscreen.height = H;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    offscreen.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'Untitled.png', { type: 'image/png' });
      loadFile(file);
    }, 'image/png');
  }, [loadFile]);

  /** Save the current canvas to either Local or Fias, awaiting completion.
   *  Used by the toolbar Save handler and the close-confirmation Save
   *  action. For local saves, prefers a stored FileSystemFileHandle
   *  (overwrites the original file in place); falls back to a download in
   *  browsers without the File System Access API. Returns false on failure
   *  and surfaces the underlying error via `saveError` for the caller to
   *  display. */
  const quickSaveCurrent = useCallback(
    async (destination: SaveDestination): Promise<boolean> => {
      const fileName = state.fileName || 'Untitled';
      setSaveError(null);
      try {
        if (destination === 'local') {
          const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
          const handle = activeTab?.fileHandle ?? null;
          if (handle) {
            const blob = await canvas.exportToBlob('png', 1);
            if (!blob) {
              setSaveError('Failed to encode the canvas as PNG.');
              return false;
            }
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
          } else {
            await exportToLocal(fileName, 'png', 1);
          }
        } else {
          const dataUrl = canvas.exportToDataURL('png', 1);
          const result = await exportToFias({
            sourceDataUrl: dataUrl,
            operations: [],
            output: { format: 'png' },
            saveAs: { fileName },
          });
          if (!result) {
            setSaveError('Failed to upload to Fias storage.');
            return false;
          }
        }
        markClean();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save failed.';
        setSaveError(message);
        console.warn('[image-editor] save failed:', err);
        return false;
      }
    },
    [state.fileName, exportToLocal, exportToFias, canvas, markClean, tabs.tabs, tabs.activeTabId],
  );

  // In-place save (toolbar Save / Ctrl+S / file menu Save). Routes through
  // quickSaveCurrent (which uses the FSA handle for local or processImage
  // for Fias). Falls back to Save As when neither destination is known —
  // the very first save of a brand-new doc, for example.
  const handleToolbarSave = useCallback(async () => {
    if (!tabs.activeTabId) return;
    const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
    if (!activeTab) return;
    const canSaveInPlace = activeTab.fileId !== null || activeTab.fileHandle !== null;
    if (!canSaveInPlace) {
      setExportDialogDestination(activeTab.fileId ? 'fias' : 'local');
      setExportDialogOpen(true);
      return;
    }
    const destination: SaveDestination = activeTab.fileId ? 'fias' : 'local';
    await quickSaveCurrent(destination);
  }, [tabs.activeTabId, tabs.tabs, quickSaveCurrent]);

  const handleSaveAs = useCallback(() => {
    const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
    setExportDialogDestination(activeTab?.fileId ? 'fias' : 'local');
    setExportDialogOpen(true);
  }, [tabs.activeTabId, tabs.tabs]);

  // Tab bar handlers
  const handleTabSwitch = useCallback(
    (id: string) => {
      tabs.setActiveTabId(id);
    },
    [tabs],
  );

  const handleTabClose = useCallback(
    (id: string) => {
      // Don't open a second-level dialog if the export dialog or the
      // close-confirm dialog is already mounted — that creates an
      // ambiguous "what does Save-As close?" race window.
      if (exportDialogOpen || confirmCloseTabId !== null) return;
      const tab = tabs.tabs.find((t) => t.id === id);
      if (!tab) return;
      if (!tab.isDirty) {
        tabs.closeTab(id);
        return;
      }
      // Switch to the dirty tab first so the canvas reflects its content
      // when the user is asked. The tab-switch effect will load its
      // snapshot (if it has one).
      if (tabs.activeTabId !== id) tabs.setActiveTabId(id);
      setConfirmCloseTabId(id);
    },
    [tabs, exportDialogOpen, confirmCloseTabId],
  );

  /** File menu — Close the active tab. Handles the dirty-confirm flow via
   *  the same path as clicking the X on the tab. */
  const handleCloseActiveFile = useCallback(() => {
    if (tabs.activeTabId) handleTabClose(tabs.activeTabId);
  }, [tabs.activeTabId, handleTabClose]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmCloseTabId(null);
    setSaveError(null);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    if (confirmCloseTabId) tabs.closeTab(confirmCloseTabId);
    setConfirmCloseTabId(null);
    setSaveError(null);
  }, [confirmCloseTabId, tabs]);

  const handleConfirmSave = useCallback(async () => {
    if (!confirmCloseTabId) return;
    const tab = tabs.tabs.find((t) => t.id === confirmCloseTabId);
    // Save back to wherever the file came from. Fias if it has a fileId,
    // local otherwise — overrides happen in the Save-As dialog, not here.
    const destination: SaveDestination = tab?.fileId ? 'fias' : 'local';
    setIsQuickSaving(true);
    const ok = await quickSaveCurrent(destination);
    setIsQuickSaving(false);
    if (!ok) return; // Leave the dialog open so the user can retry / Save As.
    tabs.closeTab(confirmCloseTabId);
    setConfirmCloseTabId(null);
  }, [confirmCloseTabId, tabs, quickSaveCurrent]);

  const handleConfirmSaveAs = useCallback(() => {
    if (!confirmCloseTabId) return;
    // Stash the tab id so the export handlers can close the tab after a
    // successful export, then dismiss the confirm dialog and open the full
    // Save As / Export dialog with the tab's original destination prefilled.
    const tab = tabs.tabs.find((t) => t.id === confirmCloseTabId);
    closeAfterExportTabIdRef.current = confirmCloseTabId;
    setExportDialogDestination(tab?.fileId ? 'fias' : 'local');
    setConfirmCloseTabId(null);
    setExportDialogOpen(true);
  }, [confirmCloseTabId, tabs.tabs]);

  const handleExportDialogClose = useCallback(() => {
    setExportDialogOpen(false);
    // Drop any pending close-after-export request — set when Save-As was
    // chosen but cleared whether the user cancelled OR the export already
    // ran (the export handlers fire BEFORE onClose, so by the time we get
    // here a successful save has already closed the tab).
    closeAfterExportTabIdRef.current = null;
  }, []);

  const handleNewTab = useCallback(() => {
    // Same as toolbar Open — funnel through the file picker so the user
    // immediately picks what to open. (A blank tab waiting for content
    // would be confusing; both entry points go through loadFile.)
    void pickAndOpenLocalFile();
  }, [pickAndOpenLocalFile]);

  const handleFiasImageSelected = useCallback(
    async (file: { fileId: string; fileName: string; dataUrl: string }) => {
      const displayName = file.fileName.replace(/\.[^.]+$/, '');
      // Always open in a new tab (matches loadFile behavior).
      tabs.addTab({ fileName: displayName, fileId: file.fileId });
      const fitZoom = await canvas.loadImage(file.dataUrl);
      const bgLayer: Layer = {
        id: nextLayerId(),
        name: 'Background',
        type: 'image',
        visible: true,
      };
      canvas.setBackgroundLayerId(bgLayer.id);
      resetLayers([bgLayer], bgLayer.id);
      setZoom(fitZoom);
      setFileInfo(displayName, file.fileId);
      setHasImage(true);
      markClean();
      history.clearHistory();
      history.saveSnapshot();
    },
    [canvas, setZoom, setFileInfo, markClean, history, tabs, nextLayerId, resetLayers],
  );

  const handleRemoveBackground = useCallback(async () => {
    setRemoveBackgroundError(null);
    setIsRemovingBackground(true);
    try {
      await canvas.removeBackground();
      markDirty();
      history.saveSnapshot();
    } catch (err) {
      setRemoveBackgroundError(err instanceof Error ? err.message : 'Failed to remove background');
    } finally {
      setIsRemovingBackground(false);
    }
  }, [canvas, markDirty, history]);

  const handleConvertGrayscale = useCallback(() => {
    // The hook fires onObjectModified internally on success, which routes
    // through our useFabricCanvas options to markDirty + saveSnapshot.
    canvas.convertToGrayscale();
  }, [canvas]);

  return (
    <div className="flex flex-col h-screen bg-background">
      <EditorToolbar
        zoom={state.zoom}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        onUndo={() => history.undoCanvas()}
        onRedo={() => history.redoCanvas()}
        onZoomIn={() => {
          const newZoom = Math.min(state.zoom * 1.25, 10);
          setZoom(newZoom);
          canvas.setZoom(newZoom);
        }}
        onZoomOut={() => {
          const newZoom = Math.max(state.zoom / 1.25, 0.1);
          setZoom(newZoom);
          canvas.setZoom(newZoom);
        }}
        onFitToScreen={() => {
          const fitZoom = canvas.fitToContainer();
          setZoom(fitZoom ?? 1);
        }}
        onNew={handleNewBlankFile}
        onSaveAs={handleSaveAs}
        onCloseFile={handleCloseActiveFile}
        hasActiveFile={tabs.activeTabId !== null}
        onCut={handleCut}
        onCopy={handleCopy}
        onPaste={() => void handlePaste()}
        canCutCopy={canvas.hasMarqueeSelection}
        canPaste={canvas.hasClipboard}
        onSave={() => void handleToolbarSave()}
        onOpenFile={handleOpenFile}
        onOpenFias={handleOpenFias}
        gridVisible={gridVisible}
        onToggleGrid={() => setGridVisible((v) => !v)}
        onShowHelp={() => setHelpDialogOpen(true)}
      />

      {tabs.tabs.length > 0 && (
        <EditorTabBar
          tabs={tabs.tabs}
          activeTabId={tabs.activeTabId}
          onSwitch={handleTabSwitch}
          onClose={handleTabClose}
          onNewTab={handleNewTab}
        />
      )}

      <div className={`flex flex-1 overflow-hidden ${isLandscape ? 'flex-row' : 'flex-col'}`}>
        <div className="flex flex-row flex-1 overflow-hidden min-w-0 min-h-0">
          <ToolPalette
            activeTool={state.activeTool}
            onToolSelect={handleToolSelect}
            onRemoveBackground={handleRemoveBackground}
            isRemovingBackground={isRemovingBackground}
            canRemoveBackground={hasImage}
          />

          <CanvasArea
            canvasElRef={canvasElRef}
            wrapperRef={wrapperRef}
            onFileDropped={loadFile}
            onFileSelected={loadFile}
            onOpenFias={handleOpenFias}
            hasImage={hasImage}
            gridVisible={gridVisible}
            viewport={canvas.viewport}
          />
        </div>

        <div
          className={
            isLandscape
              ? 'flex flex-col h-full w-[320px] max-w-[40vw] shrink-0'
              : 'flex flex-col w-full'
          }
        >
          <PropertiesPanel
            isOpen={state.propertiesPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            activeTool={state.activeTool}
            brushSettings={state.brushSettings}
            textSettings={state.textSettings}
            shapeSettings={state.shapeSettings}
            marqueeSettings={state.marqueeSettings}
            filterSettings={state.filterSettings}
            colorUnderCursor={state.colorUnderCursor}
            cursorX={state.cursorPosition.x}
            cursorY={state.cursorPosition.y}
            selectedColor={state.brushSettings.color}
            onColorPicked={(color) => {
              updateBrushSettings({ color });
              // Wheel pick has no preceding mouse:move on the canvas, so
              // mirror the picked color into colorUnderCursor too — the
              // status-bar swatch reads that field while the eyedropper
              // tool is active.
              setCursorInfo(state.cursorPosition.x, state.cursorPosition.y, color);
            }}
            onUpdateBrush={updateBrushSettings}
            onUpdateText={updateTextSettings}
            onUpdateShape={updateShapeSettings}
            onUpdateMarquee={updateMarqueeSettings}
            onUpdateFilter={updateFilterSettings}
            onConvertGrayscale={handleConvertGrayscale}
            onRotateLeft={() => canvas.rotateCanvas(-90)}
            onRotateRight={() => canvas.rotateCanvas(90)}
            onFlipHorizontal={() => canvas.flipCanvas('horizontal')}
            onFlipVertical={() => canvas.flipCanvas('vertical')}
            onApplyCrop={handleApplyCrop}
            onToggle={togglePropertiesPanel}
          />
          <LayersPanel
            isOpen={state.layersPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            layers={state.layers}
            activeLayerId={state.activeLayerId}
            onAddLayer={handleAddLayer}
            onSelectLayer={handleSelectLayer}
            onToggleVisibility={handleToggleLayerVisibility}
            onRenameLayer={handleRenameLayer}
            onDeleteLayer={handleDeleteLayer}
            onReorderLayer={handleReorderLayer}
            onToggle={toggleLayersPanel}
          />
        </div>
      </div>

      <StatusBar
        zoom={state.zoom}
        canvasWidth={state.canvasWidth}
        canvasHeight={state.canvasHeight}
        cursorX={state.cursorPosition.x}
        cursorY={state.cursorPosition.y}
        colorUnderCursor={
          state.activeTool === 'eyedropper' ? state.colorUnderCursor : state.brushSettings.color
        }
      />

      {exportDialogOpen && (
        <ExportDialog
          onClose={handleExportDialogClose}
          onExportLocal={handleExportLocal}
          onExportFias={handleExportFias}
          defaultFileName={state.fileName}
          defaultDestination={exportDialogDestination}
          canvasWidth={state.canvasWidth}
          canvasHeight={state.canvasHeight}
        />
      )}

      {confirmCloseTabId !== null && (
        <ConfirmCloseDialog
          fileName={tabs.tabs.find((t) => t.id === confirmCloseTabId)?.fileName ?? ''}
          isSaving={isQuickSaving}
          saveError={saveError}
          onCancel={handleConfirmCancel}
          onDiscard={handleConfirmDiscard}
          onSave={handleConfirmSave}
          onSaveAs={handleConfirmSaveAs}
        />
      )}

      {helpDialogOpen && <IconLegendDialog onClose={() => setHelpDialogOpen(false)} />}

      <FiasImagePickerDialog
        open={fiasPickerOpen}
        onOpenChange={setFiasPickerOpen}
        onImageSelected={handleFiasImageSelected}
      />

      {/* Hidden file input for toolbar open */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) loadFile(file);
          e.target.value = '';
        }}
      />

      {/* Loading overlay */}
      {(isExporting || isRemovingBackground) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-background rounded-lg p-6 shadow-xl">
            <p className="text-sm">
              {isRemovingBackground ? 'Removing background…' : 'Exporting...'}
            </p>
          </div>
        </div>
      )}

      {removeBackgroundError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-destructive text-destructive-foreground rounded-md px-4 py-2 shadow-lg flex items-center gap-3">
          <span className="text-sm">{removeBackgroundError}</span>
          <button
            type="button"
            className="text-xs underline"
            onClick={() => setRemoveBackgroundError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
